import React, { useState, useEffect, useCallback } from 'react';
import { X, ArrowRight, ArrowLeft, CheckCircle, Clock, CalendarDays, Loader } from 'lucide-react';
import { DEMO_MODAL, DEMO_CHOICE, DEMO_ENTRY } from '../constants';
import { useLanguage } from '../LanguageContext';
import DemoRequestModal from './DemoRequestModal';

const API = process.env.REACT_APP_BACKEND_URL;

const EMPTY_FORM = { nom: '', email: '', societe: '', telephone: '', poste: '', message: '' };

// Jours et mois en français
const FR_DAYS   = ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'];
const FR_MONTHS = ['jan.', 'fév.', 'mar.', 'avr.', 'mai', 'jun.', 'jul.', 'août', 'sep.', 'oct.', 'nov.', 'déc.'];

// Formate une date YYYY-MM-DD → "lun. 6 oct."
function fmtDay(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  return `${FR_DAYS[d.getDay()]} ${d.getDate()} ${FR_MONTHS[d.getMonth()]}`;
}

// Renvoie les N prochains jours (en excluant aujourd'hui) en YYYY-MM-DD
function nextDays(n) {
  const days = [];
  const today = new Date();
  for (let i = 1; i <= n; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    days.push(`${y}-${m}-${day}`);
  }
  return days;
}

// Détermine si un jour (YYYY-MM-DD) est potentiellement disponible selon la config
function isDayEnabled(dateStr, config) {
  if (!config?.days) return false;
  const d = new Date(dateStr + 'T12:00:00');
  const keys = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
  const key = keys[d.getDay()];
  return config.days[key]?.active === true;
}

function validate(form, errors) {
  const errs = {};
  if (!form.nom.trim())     errs.nom     = errors.required;
  if (!form.email.trim())   errs.email   = errors.required;
  if (!form.societe.trim()) errs.societe = errors.required;
  if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = errors.email;
  return errs;
}

export default function DemoModal() {
  const { lang } = useLanguage();
  const t = DEMO_MODAL[lang];
  const te = DEMO_ENTRY[lang];
  const tc = DEMO_CHOICE[lang];

  const [open, setOpen]           = useState(false);
  const [step, setStep]           = useState(1);   // 1 = form, 2 = slot picker
  const [view, setView]           = useState('choice'); // 'choice' | 'version' | 'rdv'
  const [directVersion, setDirectVersion] = useState(null); // null | 'courtier' | 'compagnie'
  const [form, setForm]           = useState(EMPTY_FORM);
  const [sent, setSent]           = useState(false);
  const [errors, setErrors]       = useState({});

  // Disponibilités
  const [availConfig, setAvailConfig]   = useState(null);
  const [availLoaded, setAvailLoaded]   = useState(false);

  // Sélection de créneau
  const [dates]                 = useState(() => nextDays(21));
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots]               = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [booking, setBooking]           = useState(false);
  const [bookingError, setBookingError] = useState('');

  // Chargement de la config dispo à l'ouverture
  useEffect(() => {
    if (!open || availLoaded) return;
    fetch(`${API}/api/demo-booking/availability`)
      .then((r) => r.json())
      .then((d) => setAvailConfig(d))
      .catch(() => setAvailConfig({ configured: false }))
      .finally(() => setAvailLoaded(true));
  }, [open, availLoaded]);

  useEffect(() => {
    const handler = () => { setOpen(true); setSent(false); setForm(EMPTY_FORM); setErrors({}); setStep(1); setView('choice'); setDirectVersion(null); setSelectedDate(null); setSlots([]); setSelectedSlot(null); setBookingError(''); };
    window.addEventListener('openDemoModal', handler);
    return () => window.removeEventListener('openDemoModal', handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const handleClose = () => {
    setOpen(false); setSent(false); setForm(EMPTY_FORM); setErrors({});
    setStep(1); setView('choice'); setDirectVersion(null);
    setSelectedDate(null); setSlots([]); setSelectedSlot(null); setBookingError('');
  };

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: '' }));
  };

  // Charge les créneaux pour une date
  const loadSlots = useCallback(async (dateStr) => {
    setLoadingSlots(true);
    setSlots([]);
    setSelectedSlot(null);
    try {
      const r = await fetch(`${API}/api/demo-booking/slots?date=${dateStr}`);
      const d = await r.json();
      setSlots(d.slots || []);
    } catch { setSlots([]); } finally { setLoadingSlots(false); }
  }, []);

  const selectDate = (dateStr) => {
    setSelectedDate(dateStr);
    loadSlots(dateStr);
  };

  // Soumission étape 1
  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate(form, t.errors);
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    if (availConfig?.configured) {
      setStep(2);
      return;
    }
    // Pas de dispo configurée → flux legacy
    submitLegacy();
  };

  const submitLegacy = () => {
    try {
      fetch(`${API}/api/demo-requests/form`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nom: form.nom, email: form.email, societe: form.societe, telephone: form.telephone || '', poste: form.poste || '', message: form.message || '' }),
      }).catch(() => {});
    } catch (_) { /* noop */ }
    const bl      = t.bodyLabels;
    const subject = encodeURIComponent(`${t.subjectPrefix} — ${form.societe}`);
    const body    = encodeURIComponent(`${bl.nom} : ${form.nom}\n${bl.email} : ${form.email}\n${bl.societe} : ${form.societe}\n${bl.telephone} : ${form.telephone || '—'}\n${bl.poste} : ${form.poste || '—'}\n${bl.message} : ${form.message || '—'}`);
    window.open(`mailto:cockpit@pitboardsuite.com?subject=${subject}&body=${body}`, '_blank');
    setSent(true);
  };

  // Réservation étape 2
  const handleBook = async () => {
    if (!selectedDate || !selectedSlot) return;
    setBooking(true);
    setBookingError('');
    try {
      const res = await fetch(`${API}/api/demo-booking/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nom: form.nom, email: form.email,
          societe: form.societe, telephone: form.telephone || '', poste: form.poste || '',
          message: form.message || '',
          date_str: selectedDate,
          slot_start: selectedSlot.start,
          slot_end: selectedSlot.end,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur lors de la réservation.');
      setSent(true);
    } catch (e) {
      setBookingError(e.message || 'Une erreur est survenue.');
    } finally { setBooking(false); }
  };

  if (!open) return null;

  // Flux « Tester en direct » : on délègue au formulaire de code OTP
  if (directVersion) {
    return <DemoRequestModal version={directVersion} onClose={handleClose} />;
  }

  // Jours actifs (selon config weekday)
  const enabledDates = availConfig?.configured
    ? dates.filter((d) => isDayEnabled(d, availConfig.config))
    : [];

  return (
    <div className="pb-modal-overlay" onClick={handleClose} data-testid="pb-demo-modal-overlay">
      <div className="pb-modal-box" onClick={(e) => e.stopPropagation()} data-testid="pb-demo-modal"
        style={{ maxWidth: (view === 'choice' || view === 'version' || step === 2) ? 560 : 480 }}>
        <button className="pb-modal-close" onClick={handleClose} aria-label={t.closeBtn} data-testid="pb-demo-modal-close">
          <X size={18} />
        </button>

        {/* ── Étape 0 : choix du mode de démo ── */}
        {view === 'choice' ? (
          <>
            <div style={{ marginBottom: 24 }}>
              <div className="pb-label" style={{ marginBottom: 10 }}>{te.label}</div>
              <h3 className="pb-modal-title">{te.title}</h3>
              <p className="pb-modal-sub">{te.sub}</p>
            </div>
            <div className="pb-demo-choice-grid pb-demo-entry-grid">
              <div className="pb-demo-choice-card" data-testid="pb-demo-entry-direct">
                <div className="pb-demo-choice-tag pb-demo-choice-tag--courtier">{te.direct.tag}</div>
                <h2 className="pb-demo-choice-title">{te.direct.title}</h2>
                <p className="pb-demo-choice-desc">{te.direct.desc}</p>
                <div className="pb-demo-choice-footer">
                  <button className="pb-btn-outline pb-demo-choice-cta" data-testid="pb-demo-entry-direct-btn" onClick={() => setView('version')}>
                    {te.direct.cta} <ArrowRight size={15} />
                  </button>
                </div>
              </div>
              <div className="pb-demo-choice-card pb-demo-choice-card--featured" data-testid="pb-demo-entry-team">
                <div className="pb-demo-choice-tag pb-demo-choice-tag--compagnie">{te.team.tag}</div>
                <h2 className="pb-demo-choice-title">{te.team.title}</h2>
                <p className="pb-demo-choice-desc">{te.team.desc}</p>
                <div className="pb-demo-choice-footer">
                  <button className="pb-btn-primary pb-demo-choice-cta" data-testid="pb-demo-entry-team-btn" onClick={() => setView('rdv')}>
                    {te.team.cta} <ArrowRight size={15} />
                  </button>
                </div>
              </div>
            </div>
          </>

        ) : view === 'version' ? (
          <>
            <button
              onClick={() => setView('choice')}
              style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#6b7280', marginBottom: 14, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              data-testid="pb-demo-entry-back"
            >
              <ArrowLeft size={14} /> {te.back}
            </button>
            <div style={{ marginBottom: 24 }}>
              <h3 className="pb-modal-title">{te.versionTitle}</h3>
              <p className="pb-modal-sub">{te.versionSub}</p>
            </div>
            <div className="pb-demo-choice-grid pb-demo-entry-grid">
              <div className="pb-demo-choice-card" data-testid="pb-demo-entry-courtier">
                <div className="pb-demo-choice-tag pb-demo-choice-tag--courtier">{tc.courtier.tag}</div>
                <h2 className="pb-demo-choice-title">{tc.courtier.title}</h2>
                <p className="pb-demo-choice-desc">{tc.courtier.desc}</p>
                <div className="pb-demo-choice-footer">
                  <button className="pb-btn-outline pb-demo-choice-cta" data-testid="pb-demo-entry-courtier-btn" onClick={() => setDirectVersion('courtier')}>
                    {tc.courtier.cta} <ArrowRight size={15} />
                  </button>
                </div>
              </div>
              <div className="pb-demo-choice-card pb-demo-choice-card--featured" data-testid="pb-demo-entry-compagnie">
                <div className="pb-demo-choice-tag pb-demo-choice-tag--compagnie">{tc.compagnie.tag}</div>
                <h2 className="pb-demo-choice-title">{tc.compagnie.title}</h2>
                <p className="pb-demo-choice-desc">{tc.compagnie.desc}</p>
                <div className="pb-demo-choice-footer">
                  <button className="pb-btn-primary pb-demo-choice-cta" data-testid="pb-demo-entry-compagnie-btn" onClick={() => setDirectVersion('compagnie')}>
                    {tc.compagnie.cta} <ArrowRight size={15} />
                  </button>
                </div>
              </div>
            </div>
          </>

        ) : sent ? (
          <div className="pb-modal-success" data-testid="pb-demo-modal-success">
            <div className="pb-modal-success-icon"><CheckCircle size={44} strokeWidth={1.5} /></div>
            <h3 className="pb-modal-success-title">
              {selectedSlot ? 'Rendez-vous confirmé !' : t.successTitle}
            </h3>
            <p className="pb-modal-success-msg">
              {selectedSlot
                ? `Votre RDV du ${fmtDay(selectedDate)} à ${selectedSlot.start} est enregistré. Vous recevrez une confirmation par e-mail.`
                : t.successMsg}
            </p>
            <button className="pb-btn-primary" onClick={handleClose} style={{ marginTop: 28 }} data-testid="pb-demo-success-close-btn">
              {t.closeBtn}
            </button>
          </div>

        ) : step === 1 ? (
          /* ── Étape 1 : formulaire ── */
          <>
            <button
              onClick={() => setView('choice')}
              style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#6b7280', marginBottom: 14, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              data-testid="pb-demo-rdv-back"
            >
              <ArrowLeft size={14} /> {te.back}
            </button>
            <div style={{ marginBottom: 28 }}>
              <div className="pb-label" style={{ marginBottom: 10 }}>{t.label}</div>
              <h3 className="pb-modal-title">{t.title}</h3>
              <p className="pb-modal-sub">{t.sub}</p>
            </div>
            <form className="pb-modal-form" onSubmit={handleSubmit} noValidate>
              {t.fields.map((f) => (
                <div key={f.key} className="pb-modal-field">
                  <label className="pb-modal-label" htmlFor={`pb-field-${f.key}`}>
                    {f.label}
                    {f.required && <span className="pb-modal-req" aria-hidden="true">*</span>}
                  </label>
                  {f.type === 'textarea' ? (
                    <textarea
                      id={`pb-field-${f.key}`}
                      className={`pb-modal-input${errors[f.key] ? ' pb-modal-input--error' : ''}`}
                      placeholder={f.placeholder}
                      value={form[f.key]}
                      onChange={(e) => handleChange(f.key, e.target.value)}
                      data-testid={`pb-demo-field-${f.key}`}
                      rows={3}
                      style={{ resize: 'vertical', minHeight: 80 }}
                    />
                  ) : (
                    <input
                      id={`pb-field-${f.key}`}
                      type={f.type}
                      className={`pb-modal-input${errors[f.key] ? ' pb-modal-input--error' : ''}`}
                      placeholder={f.placeholder}
                      value={form[f.key]}
                      onChange={(e) => handleChange(f.key, e.target.value)}
                      data-testid={`pb-demo-field-${f.key}`}
                      autoComplete={f.key === 'email' ? 'email' : 'off'}
                    />
                  )}
                  {errors[f.key] && <span className="pb-modal-error" role="alert">{errors[f.key]}</span>}
                </div>
              ))}
              <button
                type="submit"
                className="pb-btn-primary pb-modal-submit"
                data-testid="pb-demo-submit-btn"
                style={{
                  background: '#0A192F', color: '#fff',
                  width: '100%', justifyContent: 'center',
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '14px 30px', borderRadius: 8, border: 'none',
                  fontSize: 15, fontWeight: 500, cursor: 'pointer', marginTop: 4,
                }}
              >
                {availConfig?.configured ? 'Choisir un créneau' : t.submitBtn}
                <ArrowRight size={16} />
              </button>
            </form>
          </>

        ) : (
          /* ── Étape 2 : sélection de créneau ── */
          <>
            <div style={{ marginBottom: 20 }}>
              <button
                onClick={() => setStep(1)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#6b7280', marginBottom: 14, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                data-testid="pb-booking-back"
              >
                <ArrowLeft size={14} /> Retour
              </button>
              <div className="pb-label" style={{ marginBottom: 8 }}>Étape 2 sur 2</div>
              <h3 className="pb-modal-title" style={{ fontSize: 20, marginBottom: 4 }}>
                <CalendarDays size={18} style={{ display: 'inline', marginRight: 8, verticalAlign: 'middle' }} />
                Choisissez votre créneau
              </h3>
              <p style={{ fontSize: 13, color: '#6b7280' }}>
                Bonjour <strong>{form.nom}</strong> — sélectionnez une date puis un horaire.
              </p>
            </div>

            {/* Liste des jours disponibles */}
            <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 6, marginBottom: 16 }}>
              {enabledDates.length === 0 ? (
                <p style={{ fontSize: 13, color: '#9ca3af' }}>Aucune disponibilité configurée.</p>
              ) : enabledDates.map((dateStr) => (
                <button
                  key={dateStr}
                  onClick={() => selectDate(dateStr)}
                  data-testid="pb-booking-date"
                  style={{
                    flexShrink: 0, padding: '8px 14px', borderRadius: 10, fontSize: 12, fontWeight: 600,
                    border: selectedDate === dateStr ? '2px solid #0a1628' : '1.5px solid #e5e7eb',
                    background: selectedDate === dateStr ? '#0a1628' : '#fff',
                    color: selectedDate === dateStr ? '#fff' : '#374151',
                    cursor: 'pointer', transition: 'all .15s',
                  }}
                >
                  {fmtDay(dateStr)}
                </button>
              ))}
            </div>

            {/* Créneaux du jour sélectionné */}
            {selectedDate && (
              <div style={{ marginBottom: 20 }}>
                {loadingSlots ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#9ca3af', fontSize: 13 }}>
                    <Loader size={14} className="animate-spin" /> Chargement des créneaux…
                  </div>
                ) : slots.length === 0 ? (
                  <p style={{ fontSize: 13, color: '#9ca3af' }}>Aucun créneau disponible ce jour-là.</p>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {slots.map((s) => (
                      <button
                        key={s.start}
                        onClick={() => setSelectedSlot(s)}
                        data-testid="pb-booking-slot"
                        style={{
                          padding: '8px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                          border: selectedSlot?.start === s.start ? '2px solid #0055a4' : '1.5px solid #e5e7eb',
                          background: selectedSlot?.start === s.start ? '#eff6ff' : '#f9fafb',
                          color: selectedSlot?.start === s.start ? '#0055a4' : '#374151',
                          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
                          transition: 'all .15s',
                        }}
                      >
                        <Clock size={12} /> {s.start}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Récap + bouton confirmer */}
            {selectedSlot && (
              <div style={{ background: '#f0f7ff', border: '1.5px solid #bfdbfe', borderRadius: 12, padding: '14px 16px', marginBottom: 16 }}>
                <p style={{ fontSize: 13, color: '#1e40af', fontWeight: 600, margin: 0 }}>
                  RDV le {fmtDay(selectedDate)} à {selectedSlot.start} ({availConfig?.config?.slot_duration_minutes || 30} min)
                </p>
              </div>
            )}

            {bookingError && (
              <p style={{ fontSize: 13, color: '#dc2626', marginBottom: 12 }}>{bookingError}</p>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                onClick={handleBook}
                disabled={!selectedSlot || booking}
                className="pb-btn-primary"
                data-testid="pb-booking-confirm"
                style={{
                  background: '#0A192F', color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  width: '100%', padding: '14px 30px', borderRadius: 8, border: 'none',
                  fontSize: 15, fontWeight: 500, cursor: !selectedSlot || booking ? 'not-allowed' : 'pointer',
                  opacity: !selectedSlot || booking ? 0.5 : 1,
                }}
              >
                {booking ? 'Réservation…' : 'Confirmer le rendez-vous'}
                {!booking && <ArrowRight size={16} />}
              </button>
              <button
                onClick={submitLegacy}
                style={{ fontSize: 12, color: '#9ca3af', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                data-testid="pb-booking-legacy-fallback"
              >
                Préférez-vous nous contacter par e-mail ?
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
